import { ChevronDown, CheckCircle2, Clock, XCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { JobStatus } from '@repo/types';
import { cn } from '@/lib/utils';

interface JobStatusUpdaterProps {
  currentStatus: JobStatus;
  onStatusChange: (status: JobStatus) => void;
  isUpdating?: boolean;
}

const statusConfig: Record<
  JobStatus,
  {
    label: string;
    description: string;
    icon: React.ElementType;
    badgeVariant: 'default' | 'secondary' | 'destructive';
    iconColor: string;
  }
> = {
  [JobStatus.Draft]: {
    label: 'Draft',
    description: 'Not publicly visible',
    icon: Clock,
    badgeVariant: 'secondary',
    iconColor: 'text-slate-500',
  },
  [JobStatus.Active]: {
    label: 'Active',
    description: 'Accepting applications',
    icon: CheckCircle2,
    badgeVariant: 'default',
    iconColor: 'text-green-600',
  },
  [JobStatus.Expired]: {
    label: 'Expired',
    description: 'No longer active',
    icon: XCircle,
    badgeVariant: 'destructive',
    iconColor: 'text-red-600',
  },
};

export function JobStatusUpdater({
  currentStatus,
  onStatusChange,
  isUpdating = false,
}: JobStatusUpdaterProps) {
  const current = statusConfig[currentStatus];
  const CurrentIcon = current.icon;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="xs" disabled={isUpdating} className="gap-2 py-3">
          {isUpdating ? (
            <Loader2 className="h-2.5 animate-spin" />
          ) : (
            <CurrentIcon className={cn('h-2.5', current.iconColor)} />
          )}
            <span className='border-r pr-2'>
                {current.label}
            </span>
          <ChevronDown className="h-2.5 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
          Change Status
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {(Object.entries(statusConfig) as [JobStatus, typeof statusConfig[JobStatus]][]).map(
          ([status, config]) => {
            const Icon = config.icon;
            const isCurrentStatus = status === currentStatus;

            return (
              <DropdownMenuItem
                key={status}
                onClick={() => {
                  if (!isCurrentStatus && !isUpdating) {
                    onStatusChange(status);
                  }
                }}
                disabled={isCurrentStatus || isUpdating}
                className={cn(
                  'cursor-pointer',
                  isCurrentStatus && 'bg-accent'
                )}
              >
                <div className="flex w-full items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Icon className={cn('h-4 w-4', config.iconColor)} />
                    <div className="flex flex-col">
                      <span className="text-sm font-medium">{config.label}</span>
                      <span className="text-xs text-muted-foreground">
                        {config.description}
                      </span>
                    </div>
                  </div>
                  {isCurrentStatus && (
                    <CheckCircle2 className="h-2.5 text-muted-foreground" />
                  )}
                </div>
              </DropdownMenuItem>
            );
          }
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
